import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { HorizontalBar, ChartData } from 'react-chartjs-2';
import { FiPlus, FiMinus, FiUser, FiCalendar } from 'react-icons/fi';

import {
  Box,
  Text,
  Flex,
  Skeleton,
  IconButton,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
// eslint-disable-next-line
import chartjs, { ChartDataSets, ChartOptions } from 'chart.js';
import { format, parseISO } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import moment from 'moment';

import { useAuth } from '~/hooks/auth';
import { User } from '~/modules/accessControl/@types/user';
import { ActivityProps } from '~/modules/activityExecution/@types/activity';

interface PlanningChartProps {
  employee: User;
  plannings: ActivityProps[];
  date: string;
  startTimeAxis?: string;
  endTimeAxis?: string;
  loading: boolean;
  mode?: 'overallocation' | 'planning';
  selectChart?(idx: number): void;
}

const PlanningChart: React.FC<PlanningChartProps> = ({
  employee,
  plannings,
  date,
  startTimeAxis = '00:00:00',
  endTimeAxis = '00:00:00',
  loading,
  selectChart,
  // mode = 'overallocation',
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HorizontalBar | null>(null);
  const intervalRef = useRef<number | null>(null);

  // const { user } = useAuth();

  const startDateTimeAxis = `${date} ${startTimeAxis}`;
  const endDateTimeAxis = `${date} ${endTimeAxis}`;

  const [defaultSize, setDefaultSize] = useState(220);
  const [size, setSize] = useState(defaultSize);

  const [dataChart, setDataChart] = useState<ChartData<chartjs.ChartData>>(
    {} as ChartData<chartjs.ChartData>,
  );

  const [resized, setResized] = useState(false);

  const [optionsChart, setOptionsChart] = useState<ChartOptions>();

  const actualSize = useMemo(() => {
    const percent = size / defaultSize;

    // console.log('Actual Size: ', size / defaultSize, size, defaultSize);

    return percent * 100;
    // return size;
  }, [size, defaultSize]);

  const percentValue = useMemo(() => defaultSize * 0.1, [defaultSize]);

  useEffect(() => {
    // console.log(startDateTimeAxis, endDateTimeAxis);

    if (!plannings.length) return;

    setOptionsChart({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        xAxes: [
          {
            stacked: true,
            /*  type: 'time',
            time: {
              unit: 'minute',
              displayFormats: { minute: 'HH:mm', hour: 'HH[h]' },
              stepSize: 30,
              tooltipFormat: 'HH:mm',
            }, */
            gridLines: {
              drawTicks: true,
            },
            ticks: {
              /* min: new Date(startDateTimeAxis).getTime(),
              max: new Date(endDateTimeAxis).getTime(), */
              min: moment(startDateTimeAxis).valueOf(),
              max: moment(endDateTimeAxis).valueOf(),
              fontStyle: 'bold',
              autoSkip: true,
              fontSize: 14,
              stepSize: 3600000, // 1h em ms
              maxRotation: 0,
              callback: (value): string => {
                const dt = new Date(value);
                // const minutes = dt.getMinutes();
                const hours = dt.getHours();

                // console.log(`${hours}:${minutes}`);

                return `${hours}h`;
                // return minutes === 0 ? `${hours}h` : `${minutes}`;
              },
            },
            position: 'top',
          },
        ],
        yAxes: [
          {
            type: 'category',
            stacked: true,
            ticks: {
              fontStyle: 'bold',
              fontSize: 14,
              lineHeight: 1,
              padding: 5,
              callback: (value: string) => {
                return value.length > 55 ? `${value.slice(0, 52)}...` : value;
              },
            },
            gridLines: {
              drawTicks: false,
            },
          },
        ],
      },
      tooltips: {
        callbacks: {
          label(tooltipItem, data) {
            // tooltipItem.datasetIndex - pode ser 0 por isso um ternário não resolve

            if (
              !data.datasets?.length ||
              !tooltipItem ||
              tooltipItem.datasetIndex === undefined ||
              tooltipItem.index === undefined
            )
              return '';

            const dataset: ChartDataSets =
              data.datasets[tooltipItem.datasetIndex];

            const value: any = dataset.data?.[tooltipItem.index];

            return `Início: ${new Date(value[0]).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })} - Fim: ${new Date(value[1]).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}`;
          },
          /* title(tooltipItems) {
            return tooltipItems[0].label || '';
          }, */
        },
      },
      plugins: {
        datalabels: {
          display: false,
        },
      },
      animation: {
        duration: 0,
      },
      legend: {
        display: false,
      },
    });

    const labels: string[] = [];
    const datasetsValues: Array<number[]> = [];

    plannings.forEach((plan) => {
      labels.push(`Aten. ${plan.attendance_id} - ${plan.name}`);
      // labels.push('teste');

      datasetsValues.push([
        new Date(`${plan.start_date}T${plan.start_time}`).getTime(),
        new Date(`${plan.end_date}T${plan.end_time}`).getTime(),
      ]);
    });

    setDataChart({
      labels,
      datasets: [
        {
          label: 'Superalocação',
          data: datasetsValues,
          backgroundColor: '#ff3547',
          // barThickness: 10, // Define uma espessura fixa para as barras
          // maxBarThickness: 15, // Define um limite máximo para a altura das barras
          categoryPercentage: 0.8,
          // barPercentage: 0.5,
          // borderColor: ['#00FF00', '#00FF00'],
        },
      ],
    });
  }, [plannings, startDateTimeAxis, endDateTimeAxis]);

  const updateChartSize = useCallback((): void => {
    setTimeout(() => {
      if (chartRef.current) {
        chartRef.current.chartInstance.resize();
      }
    }, 50);

    /* if (!resized) setResized(true); */
  }, []);

  const handleSubSize = useCallback(() => {
    if (!resized) {
      const clientHeight = divRef.current?.clientHeight || defaultSize;

      setDefaultSize(clientHeight);

      setSize(clientHeight - clientHeight * 0.1);
      setResized(true);
    } else {
      setSize((state) => {
        const newSize = state - percentValue;

        return newSize >= 0 ? newSize : 0;
      });
    }

    updateChartSize();
  }, [updateChartSize, defaultSize, resized, percentValue]);

  const handleAddSize = useCallback(() => {
    // console.log(resized);

    if (!resized) {
      const clientHeight = divRef.current?.clientHeight || defaultSize;

      setDefaultSize(clientHeight);

      setSize(clientHeight + clientHeight * 0.1);
      setResized(true);
    } else {
      setSize((state) => state + percentValue);
    }

    updateChartSize();
  }, [updateChartSize, defaultSize, resized, percentValue]);

  const stopIncrement = (): void => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const handleElementChart = useCallback(
    (chartElement) => {
      // console.log(chartElement);

      if (chartElement[0]?._view) {
        const index = chartElement[0]?._index;

        if (selectChart) selectChart(index);
      }
    },
    [selectChart],
  );

  return (
    <>
      <Box
        minHeight={`${size}px`}
        maxHeight={resized ? `${size}px` : undefined}
        width="full"
      >
        <>
          <Flex alignItems="center" mb={2}>
            <Flex alignItems="center">
              <FiUser />
              <Text mx={2} fontWeight="bold">
                Funcionário:
              </Text>
            </Flex>
            {loading ? (
              <Skeleton height={6} width="150px" />
            ) : (
              <Text>{employee.name}</Text>
            )}
          </Flex>
          <Flex alignItems="center">
            <Flex alignItems="center">
              <FiCalendar />
              <Text mx={2} fontWeight="bold">
                Data:
              </Text>
            </Flex>
            {loading ? (
              <Skeleton height={6} width="150px" />
            ) : (
              <Text>
                {format(parseISO(date), "dd 'de' MMMM 'de' yyyy", {
                  locale: ptBR,
                })}
              </Text>
            )}
          </Flex>
          {loading ? (
            <Box w="full" mt={2}>
              <Skeleton height="250px" width="calc(100% - 30px)" />
            </Box>
          ) : (
            <>
              <Flex
                w="full"
                justifyContent="flex-end"
                alignItems="center"
                mb={2}
              >
                <IconButton
                  aria-label="sub"
                  icon={<FiMinus />}
                  isRound
                  size="xs"
                  onClick={handleSubSize}
                  onMouseDown={() => {
                    if (!intervalRef.current) {
                      intervalRef.current = setInterval(() => {
                        handleSubSize();
                        // console.log('aumentando');
                      }, 100);
                    }
                  }}
                  onMouseUp={stopIncrement}
                  onMouseLeave={stopIncrement}
                />
                <Text mx={2} width="45px" textAlign="center">
                  {`${actualSize.toFixed(0)}%`}
                </Text>
                <IconButton
                  aria-label="add"
                  icon={<FiPlus />}
                  isRound
                  size="xs"
                  onClick={handleAddSize}
                  onMouseDown={() => {
                    if (!intervalRef.current) {
                      intervalRef.current = setInterval(() => {
                        handleAddSize();
                        // console.log('aumentando');
                      }, 100);
                    }
                  }}
                  onMouseUp={stopIncrement}
                  onMouseLeave={stopIncrement}
                  // disabled={!resized}
                />
              </Flex>
              {!loading && !!plannings.length ? (
                <Box
                  ref={divRef}
                  minHeight={`${size}px`}
                  maxHeight={resized ? `${size}px` : undefined}
                >
                  <HorizontalBar
                    ref={chartRef}
                    data={dataChart}
                    options={optionsChart}
                    getElementAtEvent={(e) => {
                      if (selectChart) handleElementChart(e);
                    }}
                    redraw={false}
                  />
                </Box>
              ) : (
                <Alert status="info" marginTop="30px">
                  <AlertIcon />
                  <Text>
                    Não foi encontrada nenhuma atividade com superalocação para
                    essa data
                  </Text>
                </Alert>
              )}
            </>
          )}
        </>
      </Box>
    </>
  );
};

export default PlanningChart;
